import { Component, Host, h, Prop, Event, Watch, EventEmitter, Element } from '@stencil/core';
import { slotHasAnyContent } from '../../utils/slotHelpers';
import moveSlotChildElementsToLightDom from '../../utils/moveSlotChildElementsToLightDom';
import { SwitchType } from './types';

export interface AhaSwitch extends HTMLAhaSwitchElement {}

/**
 * @slot DEFAULT - Contents that always appear to the right of a switch. Useful for single labels that will not change based on the state of the switch. Can be used in combination with the other slots listed.
 * @slot active - Contents that appear only when the switch's "active" state is set to `true`.
 * @slot inactive - Contents that appear only when the switch's "active" state is set to `false`.
 */
@Component({
  tag: 'aha-switch',
  styleUrl: 'aha-switch.css',
  shadow: true,
})
export class AhaSwitch {
  @Element() host!: HTMLAhaSwitchElement;

  private hiddenInputElem?: HTMLInputElement;

  /**
   * The type of the switch. The main difference is what is sent to the server when
   * the form is submitted. Possible values are:
   *
   * - `checkbox` - A checkbox switch is a standard HTML checkbox. When the switch
   *                is active, this will send the activeValue(default: 1) to the server.
   *                When the switch is inactive, this will SEND NOTHING to the server.
   *
   * - `hidden` - A hidden input on the page. When the switch is active, this will
   *              send the activeValue(default: 1) when the form is submitted.
   *              When the switch is inactive, this will send the
   *              inactiveValue(default: 0) when the form is submitted.
   */
  @Prop({ reflect: true }) type: SwitchType = 'hidden';

  /** The state the switch is in */
  @Prop({ reflect: true }) active?: boolean;

  @Prop({ reflect: true }) activeValue: string | number = 1;

  @Prop({ reflect: true }) inactiveValue: string | number = 0;

  /**
   * Triggers when `<aha-switch>` is set to the "active" or "inactive" states.
   */
  @Event({
    eventName: 'aha-switch:change',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  change!: EventEmitter<HTMLAhaSwitchElement>;

  @Watch('active')
  fireChangedEvent() {
    // Fire an event on the document to let any listener know that the `<aha-switch>` active state has been changed.
    this.change.emit(this.host);
  }

  /** Whether or not the switch can be activated */
  @Prop({ reflect: true }) disabled?: boolean;

  /** The name of the form control element that will be submitted along with a `<form>` submit. When submitted to a form, an `<aha-switch>` component will send a value of `0` if inactive, or `1` if active. */
  @Prop({ reflect: true }) name?: string;

  /** Whether or not the component is read only.  */
  @Prop({ reflect: true, attribute: 'readonly' }) readOnly?: boolean;

  private handleClick(event: MouseEvent) {
    const target = event.currentTarget as HTMLAhaSwitchElement;
    if (target.disabled || target.readOnly) return;
    target.active = !target.active;
  }

  /** Toggle the active state of an `<aha-switch>` */
  private toggle() {
    this.active = !this.active;
  }

  /** Activate an `<aha-switch>` */
  private activate() {
    this.active = true;
  }

  /** Deactivate an `<aha-switch>` */
  private deactivate() {
    this.active = false;
  }

  private handleKeyup(event: KeyboardEvent) {
    if (this.disabled) return;
    if (event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    } else if (event.key === 'Enter') {
      const form = event
        .composedPath()
        .find((node: any) => node.nodeName === 'FORM') as HTMLFormElement;
      if (form && !form.disabled) form.requestSubmit();
    }
  }

  private handleSlotChange(event: any) {
    const host = event.target.getRootNode().host;
    host.hideSlotsIfEmpty();
  }

  private slotsWithContent = new Set();

  private slotsElem!: HTMLElement;

  private hideSlotsIfEmpty() {
    this.slotsElem?.querySelectorAll('slot').forEach((slot: HTMLSlotElement) => {
      if (slotHasAnyContent(slot)) {
        slot.hidden = false;
        this.slotsWithContent.add(slot);
      } else {
        slot.hidden = true;
        this.slotsWithContent.delete(slot);
      }
    });

    this.slotsWithContent.size
      ? this.slotsElem?.removeAttribute('hidden')
      : this.slotsElem?.setAttribute('hidden', '');
  }
  componentDidRender() {
    this.hideSlotsIfEmpty();
  }

  private checkBoxInput() {
    return (
      <input
        ref={el => (this.hiddenInputElem = el)}
        type="checkbox"
        value={this.active ? this.activeValue : undefined}
        name={this.name}
        readOnly={this.readOnly}
        disabled={this.disabled}
        style={{ display: 'none' }}
        checked={this.active}
      />
    );
  }

  private hiddenInput() {
    return (
      <input
        ref={el => (this.hiddenInputElem = el)}
        type="hidden"
        value={this.active ? this.activeValue : this.inactiveValue}
        name={this.name}
        readOnly={this.readOnly}
        disabled={this.disabled}
      />
    );
  }

  componentDidLoad() {
    // Move nodes inside slots into the light DOM. Used for `<form>` submission.
    moveSlotChildElementsToLightDom(this.host, 'form-control');
  }

  render() {
    return (
      <Host onClick={this.handleClick} tabIndex={0} onKeyup={this.handleKeyup}>
        <div id="switch" part="switch">
          <div id="handle" />
        </div>
        <div id="slots" ref={ref => (this.slotsElem = ref as HTMLElement)}>
          <slot onSlotchange={this.handleSlotChange}></slot>
          <slot
            name="inactive"
            aria-disabled={this.active}
            onSlotchange={this.handleSlotChange}
          ></slot>
          <slot
            name="active"
            aria-disabled={!this.active}
            onSlotchange={this.handleSlotChange}
          ></slot>
        </div>
        <slot name="form-control">
          {this.type === 'checkbox' ? this.checkBoxInput() : this.hiddenInput()}
        </slot>
      </Host>
    );
  }
}
